import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';

interface Referral {
  id: string;
  referral_code: string;
  // SECURITY: referred_email / referred_name intentionally omitted - column-level
  // REVOKE on public.referrals blocks them for the referrer.
  status: string;
  reward_cents: number;
  created_at: string;
  activated_at: string | null;
}

interface ReferralReward {
  id: string;
  reward_type: 'referrer' | 'referred';
  amount_cents: number;
  status: string;
  created_at: string;
}

interface LeaderboardEntry {
  referrer_id: string;
  display_name: string;
  completed_referrals: number;
  total_earned_cents: number;
}

interface ReferralStats {
  totalReferrals: number;
  completedReferrals: number;
  pendingReferrals: number;
  totalEarnedCents: number;
}

export function useReferrals(user: User | null) {
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [rewards, setRewards] = useState<ReferralReward[]>([]);
  const [stats, setStats] = useState<ReferralStats>({
    totalReferrals: 0,
    completedReferrals: 0,
    pendingReferrals: 0,
    totalEarnedCents: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Generate referral code based on user ID
  const referralCode = user ? `ELEFA${user.id.slice(0, 6).toUpperCase()}` : '';
  const referralLink = user ? `https://elefa.tel/activate?ref=${referralCode}` : '';

  useEffect(() => {
    if (!user) {
      setReferrals([]);
      setRewards([]);
      setStats({
        totalReferrals: 0,
        completedReferrals: 0,
        pendingReferrals: 0,
        totalEarnedCents: 0,
      });
      setLoading(false);
      return;
    }

    const fetchReferralData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch user's referrals (where they are the referrer)
        // SECURITY: do NOT request referred_email / referred_name - these are PII
        // and column-level REVOKE blocks them for the referrer. Selecting * would
        // 403 the whole row. Use explicit safe columns only.
        const { data: referralsData, error: referralsError } = await supabase
          .from('referrals')
          .select('id, referrer_id, referred_id, referral_code, status, reward_cents, created_at, activated_at, plan_id, updated_at')
          .eq('referrer_id', user.id)
          .order('created_at', { ascending: false });

        if (referralsError) throw referralsError;

        // Fetch user's rewards
        const { data: rewardsData, error: rewardsError } = await supabase
          .from('referral_rewards')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (rewardsError) throw rewardsError;

        const typedReferrals = (referralsData || []) as Referral[];
        const typedRewards = (rewardsData || []) as ReferralReward[];

        setReferrals(typedReferrals);
        setRewards(typedRewards);

        // Calculate stats
        const completed = typedReferrals.filter(r => r.status === 'completed').length;
        const pending = typedReferrals.filter(r => r.status === 'pending').length;
        const totalEarned = typedRewards
          .filter(r => r.status === 'credited' && r.reward_type === 'referrer')
          .reduce((sum, r) => sum + r.amount_cents, 0);

        setStats({
          totalReferrals: typedReferrals.length,
          completedReferrals: completed,
          pendingReferrals: pending,
          totalEarnedCents: totalEarned,
        });
      } catch (err) {
        console.error('Error fetching referral data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load referral data');
      } finally {
        setLoading(false);
      }
    };

    fetchReferralData();

    // Subscribe to realtime updates
    const referralsChannel = supabase
      .channel('referrals-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'referrals',
          filter: `referrer_id=eq.${user.id}`,
        },
        () => {
          fetchReferralData();
        }
      )
      .subscribe();

    const rewardsChannel = supabase
      .channel('rewards-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'referral_rewards',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          fetchReferralData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(referralsChannel);
      supabase.removeChannel(rewardsChannel);
    };
  }, [user]);

  // Create a new referral invite
  const createReferralInvite = async (email?: string) => {
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
      const { data, error } = await supabase
        .from('referrals')
        .insert({
          referrer_id: user.id,
          referral_code: referralCode,
          referred_email: email || null,
          status: 'pending',
          reward_cents: 0,
        })
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (err) {
      console.error('Error creating referral invite:', err);
      return { 
        success: false, 
        error: err instanceof Error ? err.message : 'Failed to create invite' 
      };
    }
  };

  return {
    referrals,
    rewards,
    stats,
    loading,
    error,
    referralCode,
    referralLink,
    createReferralInvite,
  };
}

export function useLeaderboard() {
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        // Leaderboard will be populated from actual referral data
        // when users start referring others
        setLeaderboard([]);
      } catch (err) {
        console.error('Error fetching leaderboard:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, []);

  return { leaderboard, loading };
}
